import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { Badge } from "@/components/ui/badge";
import { formatPriceExact, getPrimaryImg } from "@/utils";
import { TransferRequest } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { PERMISSIONS } from "@/routes/guards";
import { useToggleSaveListing } from "@/hooks/useSavedListings";

type Props = {
  item: TransferRequest;
};

export default function TransferRequestCard({ item }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  const img = getPrimaryImg(item.images || []);

  // Chỉ hiển thị tag trạng thái cho người tạo hoặc Admin
  const showStatusTag = !!user && (user.id === item.createdBy || user.role === 'admin');

  const title = item.preferredCategories?.length
    ? `Cần tìm ${item.preferredCategories.join(", ")}`
    : "Nhu cầu mua sang nhượng";

  return (
    <Link
      to={`/nhu-cau-sang-nhuong/${item.id}`}
      className="fnb-card block animate-fade-up h-full flex flex-col relative"
    >
      {/* Thumbnail */}
      <div className="h-44 overflow-hidden rounded-t-xl relative shrink-0">
        {img ? (
          <img
            src={img}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-6xl"
            style={{ background: "var(--brand-pale)" }}
          >
            🔍
          </div>
        )}

        <CardStatusTag
          className="absolute top-2 left-2 z-10"
          show={showStatusTag}
          status={item.approvalStatus}
          label={item.approvalStatus === "approved" ? "Đã duyệt" : "Chờ duyệt"}
        />
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-semibold text-sm leading-snug line-clamp-2 mb-3 pr-8"
          style={{ color: "var(--text-1)" }}
        >
          {title}
        </h3>

        {/* Budget */}
        <p
          className="font-display font-black text-lg mb-3"
          style={{ color: "var(--brand)" }}
        >
          {formatPriceExact(String(item.budgetMin))} - {formatPriceExact(String(item.budgetMax))} {item.currencyUnit}
        </p>

        {/* Requirements */}
        <p className="text-xs mb-3 line-clamp-2 text-gray-500">
          {item.additionalRequirements || "Không có yêu cầu thêm"}
        </p>

        {/* Type badges */}
        {item.preferredCategories && item.preferredCategories.length > 0 && (
          <div className="flex gap-1.5 mb-3 flex-wrap">
            {item.preferredCategories.slice(0, 2).map((cat, idx) => (
              <Badge key={idx} variant="secondary" className="text-[10px]">
                {cat}
              </Badge>
            ))}
            {item.preferredCategories.length > 2 && (
              <Badge variant="secondary" className="text-[10px]">
                +{item.preferredCategories.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Meta */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10"
          style={{ borderColor: "var(--border-c)" }}
        >
          {item.district && item.province && (
            <span className="fnb-tag text-[11px]">
              <MapPin size={10} /> {item.district.name}, {item.province.name}
            </span>
          )}

          {(item.preferredAreaMin > 0 || item.preferredAreaMax > 0) && (
            <span className="fnb-tag text-[11px]">
              {item.preferredAreaMin} - {item.preferredAreaMax} {item.areaUnit}
            </span>
          )}
        </div>
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "BusinessTransferRequest");
        }}
      />
    </Link>
  );
}
